import React from 'react';
import PropTypes from 'prop-types';
import { autobind } from 'core-decorators';
import BaseComponent from 'components/BaseComponent';
import styledComp from 'components/StyledComp';
import DataTables from 'components-desktop/DataTables';
import TfootSummary from 'components-desktop/TfootSummary';
import helpers from 'misc/helpers';

const propTypes = {
  title: PropTypes.string,
  scheduleVisits: PropTypes.array,
};

const defaultProps = {
  title: '',
  scheduleVisits: [],
};

@autobind
class TableVisits extends BaseComponent {
  constructor(props) {
    super(props);
  }

  createColumns() {
    return [{
      title: 'Id',
      dataIndex: 'id',
      sorter: (a, b) => a.id - b.id,
      defaultSortOrder: 'ascend',
    }, {
      title: 'Date',
      dataIndex: 'dateUsString',
      sorter: (a, b) => a.date - b.date,
    }, {
      title: 'Company',
      dataIndex: 'companyName',
      sorter: (a, b) => a.companyName?.localeCompare(b.companyName),
    }, {
      title: 'Service Code',
      dataIndex: 'serviceCode',
      sorter: (a, b) => a.serviceCode?.localeCompare(b.serviceCode),
    }, {
      title: 'Patient Id',
      dataIndex: 'patientId',
      sorter: (a, b) => a.patientId - b.patientId,
    }, {
      title: 'Patient',
      dataIndex: 'patientName',
      sorter: (a, b) => a.patientName?.localeCompare(b.patientName),
      render: (text, record) => (
        <styledComp.A
          width="wrap_content"
          height="wrap_content"
          href={`/patients/patient_profile?id=${record.patientId}`}
          target="_blank"
        >
          {text}
        </styledComp.A>
      ),
    }, {
      title: 'Caregiver Id',
      dataIndex: 'caregiverId',
      sorter: (a, b) => a.caregiverId - b.caregiverId,
    }, {
      title: 'Caregiver',
      dataIndex: 'caregiverName',
      sorter: (a, b) => a.caregiverName?.localeCompare(b.caregiverName),
      render: (text, record) => (
        <styledComp.A
          width="wrap_content"
          height="wrap_content"
          href={`/caregivers/caregiver_profile?id=${record.caregiverId}`}
          target="_blank"
        >
          {text}
        </styledComp.A>
      ),
    }, {
      title: 'Coordinator',
      dataIndex: 'patientCoordinator',
      sorter: (a, b) => a.patientCoordinator?.localeCompare(b.patientCoordinator),
    }, {
      title: 'Team',
      dataIndex: 'patientTeam',
      sorter: (a, b) => a.patientTeam?.localeCompare(b.patientTeam),
    }, {
      title: 'Sch. Times',
      dataIndex: 'scheduleTimes',
      sorter: (a, b) => a.fromTime - b.fromTime,
    }, {
      title: 'Visit Times',
      dataIndex: 'visitTimes',
      sorter: (a, b) => a.visitIn - b.visitIn,
    }, {
      title: 'Bill Hours',
      dataIndex: 'billHoursString',
      sorter: (a, b) => a.billHours - b.billHours,
    }, {
      title: 'Unit Type',
      dataIndex: 'unitType',
      sorter: (a, b) => a.unitType?.localeCompare(b.unitType),
    }, {
      title: 'Inv. #',
      dataIndex: 'invoiceNumber',
      sorter: (a, b) => a.invoiceNumber?.localeCompare(b.invoiceNumber),
      render: (text, record) => (
        <styledComp.A
          width="wrap_content"
          height="wrap_content"
          href={`/invoices/invoice_details?id=${record.invoiceId}`}
          target="_blank"
        >
          {text}
        </styledComp.A>
      ),
    }, {
      title: 'Inv. Date',
      dataIndex: 'invoiceDateUsString',
      sorter: (a, b) => a.invoiceDate - b.invoiceDate,
    }, {
      title: 'Mail Date',
      dataIndex: 'invoiceMailDateUsString',
      sorter: (a, b) => a.invoiceMailDate - b.invoiceMailDate,
    }];
  }

  createDataSource(scheduleVisits) {
    const dataSource = [];
    scheduleVisits.forEach((visit, index) => {
      dataSource.push({
        key: visit.getId(),
        id: visit.getId(),
        date: visit.getLong('date'),
        dateUsString: visit.getString('dateUsString'),
        companyName: visit.getString('companyName'),
        serviceCode: visit.getRemoteObject('service')?.getString('displayCode'),
        patientId: visit.getLong('patientId'),
        patientName: visit.getString('patientName'),
        caregiverId: visit.getLong('caregiverId'),
        caregiverName: visit.getString('caregiverName'),
        patientCoordinator: visit.getString('patientCoordinator'),
        patientTeam: visit.getString('patientTeam'),
        fromTime: visit.getLong('fromTime'),
        scheduleTimes: `${visit.getString('fromTimeString')}-${visit.getString('toTimeString')}`,
        visitIn: visit.getLong('visitIn'),
        visitTimes: `${visit.getString('visitInString') || ''}-${visit.getString('visitOutString') || ''}`,
        billHours: visit.getLong('billHours'),
        billHoursString: visit.getString('billHoursString'),
        unitType: this.e2t('serviceUnitType', visit.getRemoteObject('service')?.getString('unitType')),
        invoiceId: visit.getLong('invoiceId'),
        invoiceNumber: visit.getString('invoiceNumber'),
        invoiceDate: visit.getLong('invoiceDate'),
        invoiceDateUsString: visit.getString('invoiceDateUsString'),
        invoiceMailDate: visit.getLong('invoiceMailDate'),
        invoiceMailDateUsString: visit.getString('invoiceMailDateUsString'),
      });
    });
    return dataSource;
  }

  renderTfoot(columnsToDisplay, dataRowsToDisplay) {
    return (
      <TfootSummary
        columnsToDisplay={columnsToDisplay}
        dataRowsToDisplay={dataRowsToDisplay}
        isTableSelectable={false}
        columnKeysToAlign={['billHoursString']}
        reducers={[
          TfootSummary.SUM_REDUCER_GENERATOR('billHours'),
        ]}
        formatters={[
          n => helpers.min2ClockHour(n / 1000 / 60),
        ]}
      />
    );
  }

  renderContent() {
    const { scheduleVisits, title, ...layoutProps } = this.props;

    return (
      <DataTables
        {...layoutProps}
        ref={(ref) => { this.dataTables = ref; }}
        color="purple_wisteria"
        fontSize="12px"
        size="small"
        title={title}
        icon="check-square"
        columns={this.createColumns()}
        dataSource={this.createDataSource(scheduleVisits)}
        tfoot={this.renderTfoot}
      />
    );
  }
}

// https://facebook.github.io/react/docs/typechecking-with-proptypes.html
TableVisits.propTypes = propTypes;
TableVisits.defaultProps = defaultProps;

export default TableVisits;
